/**
 * 一斉配信の監視と緊急停止 `/campaigns`。
 *
 * **なぜ運営者面に配信の画面が要るか。** 送信元 IP のレピュテーションを持って
 * いるのは運営者であって契約者ではない。1 契約者の暴走（苦情率の急上昇・
 * 巨大リストへの誤送信・乗っ取り）を止められないと、同じノードに同居している
 * 他の契約者のメールがまとめて届かなくなる。
 *
 * **本文は出ない**（不変条件 9）。件名までは運用判断に要るので API が返すが、
 * ブロック JSON / HTML は CP の DB に無い。
 *
 * キルスイッチは **投入 1 名 / 解除は別の運営者の承認**。安全側（止める）は
 * 待たせず、危険側（動かす）だけを二名にする。この非対称を UI にも出す。
 */
import * as React from 'react';
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, Eye, EyeOff, LayoutTemplate, OctagonX, Plus, Power, Send } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  Input,
  PageHeader,
  Section,
  StatusBadge,
  Textarea,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { CAMPAIGN_STATUS, labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { useAuth, usePermission } from '../stores/auth.js';
import { FilterBar } from '../components/filter-bar.js';
import { ConfirmTextDialog, FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { PageTabs } from '../components/page-tabs.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type {
  BulkKillSwitchView,
  ContentTemplateEntry,
  CountedList,
  OpsCampaignSummary,
  StaffUser,
} from '../types/api.js';

const routeApi = getRouteApi(RID.campaigns);

/** 苦情率の警戒線（万分率）。Gmail / Yahoo! は 0.3%（30bp）で締め始める。 */
const COMPLAINT_WARN_BP = 10;
const COMPLAINT_CRITICAL_BP = 30;
/** バウンス率の警戒線。2% を超えると受信側の評価が急速に落ちる。 */
const BOUNCE_WARN_BP = 200;

export function CampaignsPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  /*
   * 共有テンプレートのタブは `ops.content.template.write` を持つ者にだけ出す。
   * 参照だけの API が無い（読みも write 権限で守られている）ので、
   * 権限が無い運営者にタブを見せると必ず 403 になる。
   */
  const canTemplates = usePermission(OPS_PERMISSION.contentTemplateWrite);

  return (
    <>
      <PageHeader
        title="一斉配信"
        description="全組織の配信状況と緊急停止。苦情率・バウンス率が上がっている配信は、同居する他の契約者にも影響します。"
      />

      {canTemplates ? (
        <PageTabs
          value={search.tab}
          onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
          tabs={[
            { value: 'campaigns', label: '配信の監視', icon: Send },
            { value: 'templates', label: '共有テンプレート', icon: LayoutTemplate },
          ]}
        />
      ) : null}

      {canTemplates && search.tab === 'templates' ? <TemplatesTab /> : <CampaignsTab />}
    </>
  );
}

function CampaignsTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canHalt = usePermission(OPS_PERMISSION.bulkHalt);
  const [halting, setHalting] = React.useState<OpsCampaignSummary | null>(null);

  const list = useCursorList<OpsCampaignSummary>({
    path: '/campaigns',
    keyFor: (params) => qk.opsCampaigns(params),
    filters: {
      q: search.q,
      organizationId: search.organizationId,
      /*
       * **`live=0` は送らない。** サーバーは `z.coerce.boolean()` で受けており、
       * `'0'` は `Boolean('0') === true` になる（空でない文字列は全部 true）。
       * 「全期間」を表す値がクライアント側に存在しないので、過去を見るときは
       * 状態を明示する（API は `status` があれば live の絞りを外す）。
       * 以前はここで `live: 0` を送っていて、**「全期間」を選んでも結果が
       * 1 行も変わらなかった**。
       */
      status: search.status,
    },
    // 動いているものを見る画面なので、開いている間は 10 秒ごとに引き直す。
    // 過去の状態を指定して見ているときは変化しないので止める。
    refetchInterval: search.status ? false : 10_000,
  });

  const columns: Array<DataTableColumn<OpsCampaignSummary>> = [
    {
      id: 'status',
      header: '状態',
      width: 'w-32',
      cell: (c) => (
        <StatusBadge status={c.status} size="sm" dot pulse={c.status === 'sending'} />
      ),
    },
    {
      id: 'name',
      header: '配信',
      cell: (c) => (
        <div className="min-w-0 space-y-0.5">
          {/*
            詳細へ。**停止が効いたかを確かめられる唯一の画面**
            （`campaign_batches` の状態別件数はここでしか見られない）。
          */}
          <Link
            to="/campaigns/$campaignId"
            params={{ campaignId: c.id }}
            className="block truncate font-medium hover:text-primary"
          >
            {c.name}
          </Link>
          {/* 件名までは出す。何を配っているか分からないと止める判断ができない。 */}
          <span className="block truncate text-xs text-muted-foreground">{c.subject}</span>
        </div>
      ),
    },
    {
      id: 'org',
      header: '契約組織',
      hideBelow: 'md',
      cell: (c) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: c.organizationId }}
          search={{ tab: 'overview' }}
          className="hover:text-primary"
        >
          {c.organizationName}
        </Link>
      ),
    },
    {
      id: 'progress',
      header: '進捗',
      align: 'right',
      cell: (c) => (
        <span className="tabular text-sm">
          {formatCount(c.sent)}
          <span className="text-muted-foreground"> / {formatCount(c.recipientCount)}</span>
          {/*
            * **注入失敗は進捗に混ぜない。** 以前は `sent` に混ざっていたので
            * 「全部送れた」ように見えていた。相手のサーバーへ渡る前に落ちた
            * 通数は原因がこちら側にあるため、0 でないときだけ目立たせる。
            */}
          {c.failed > 0 ? (
            <span className="text-destructive"> / 失敗 {formatCount(c.failed)}</span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'bounce',
      header: 'バウンス率',
      align: 'right',
      hideBelow: 'md',
      cell: (c) => <RateCell bp={c.bounceRateBp} warn={BOUNCE_WARN_BP} critical={BOUNCE_WARN_BP * 2} />,
    },
    {
      id: 'complaint',
      header: '苦情率',
      align: 'right',
      cell: (c) => (
        <RateCell bp={c.complaintRateBp} warn={COMPLAINT_WARN_BP} critical={COMPLAINT_CRITICAL_BP} />
      ),
    },
    {
      id: 'startedAt',
      header: '開始',
      hideBelow: 'lg',
      cell: (c) => formatDateTime(c.startedAt ?? c.scheduledAt),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-28',
      cell: (c) =>
        canHalt && HALTABLE.has(c.status) ? (
          <Button variant="destructive" size="sm" onClick={() => setHalting(c)}>
            <OctagonX aria-hidden />
            停止
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <div className="mb-3 flex justify-end">
        <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
      </div>

      <KillSwitchPanel canHalt={canHalt} />

      <FilterBar
        q={search.q}
        onQChange={(q) => void navigate({ search: (prev) => ({ ...prev, q }) })}
        placeholder="配信名・組織名で絞り込む"
        active={hasActiveFilter(search, ['tab'])}
        onClear={() =>
          void navigate({
            search: (prev) => ({ ...prev, q: undefined, organizationId: undefined, status: undefined }),
          })
        }
        selects={[
          {
            key: 'status',
            label: '状態',
            value: search.status,
            // 未指定＝サーバーの既定（進行中のみ）。ここが「全期間」の代わり。
            allLabel: '進行中のみ',
            options: CAMPAIGN_STATUS.map((s) => ({ value: s, label: labelOf(s) })),
            onChange: (v) => void navigate({ search: (prev) => ({ ...prev, status: v }) }),
          },
        ]}
      />

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(c) => c.id}
        loading={list.query.isLoading}
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search, ['tab'])}
            entityLabel={search.status ? `${labelOf(search.status)}の配信` : '進行中の配信'}
            onClearFilter={() =>
              void navigate({
                search: (prev) => ({ ...prev, q: undefined, organizationId: undefined, status: undefined }),
              })
            }
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <HaltDialog campaign={halting} onClose={() => setHalting(null)} />
    </>
  );
}

/** API の `HALTABLE_STATUSES` と同じ集合。片方だけ増やすと 400 になる。 */
const HALTABLE: ReadonlySet<string> = new Set([
  'scheduled',
  'sending',
  'paused',
  'awaiting_release',
  'validated',
  'test_sent',
]);

/** 万分率をパーセント表示にして、閾値で色を変える。 */
function RateCell({
  bp,
  warn,
  critical,
}: {
  bp: number;
  warn: number;
  critical: number;
}): React.ReactElement {
  const tone = bp >= critical ? 'text-destructive' : bp >= warn ? 'text-warning' : undefined;
  return <span className={tone}>{(bp / 100).toFixed(2)} %</span>;
}

/* ----------------------------------------------------------- 緊急停止 */

function HaltDialog({
  campaign,
  onClose,
}: {
  campaign: OpsCampaignSummary | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (campaign) setReason('');
  }, [campaign]);

  const navigate = useNavigate();
  const halt = useMutation({
    mutationFn: () => api.post(`/campaigns/${campaign?.id ?? ''}/halt`, { reason: reason.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'campaigns'] });
      const id = campaign?.id;
      onClose();
      /*
       * **止めたら必ず詳細へ送る。** 一覧に戻すと「押した」だけで終わり、
       * 払い出し済みのバッチが残っているかを確かめないまま次の対応へ移る。
       * 詳細画面のバッチ表が 0 になって初めて止まったと言える。
       */
      if (id) void navigate({ to: '/campaigns/$campaignId', params: { campaignId: id } });
    },
  });

  return (
    <FormDialog
      open={campaign !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="この配信を緊急停止する"
      description={
        campaign
          ? `${campaign.organizationName} / ${campaign.name}（未送信 ${formatCount(campaign.recipientCount - campaign.sent)} 件）`
          : undefined
      }
      submitLabel="停止する"
      pending={halt.isPending}
      error={halt.error}
      // サーバー側の zod は 5 文字以上。先に止めて 400 を出さない。
      disabled={reason.trim().length < 5}
      onSubmit={() => halt.mutate()}
    >
      {/*
        **「ノードへも即座に伝わる」と書かないこと。** 停止が同一トランザクションで
        潰すのは `campaign_batches` / `campaign_recipients` の **`pending`（未払い出し）だけ**で
        （`apps/api/src/routes/ops/campaigns.ts` の halt）、**既にノードが持って
        いったバッチ（`leased`）を取り消す経路は現在存在しない**（`bulk/lease` の
        応答にも `node_commands` にも取り消しの動詞が無い）。
        画面が「破棄されます」と断言すると、運営はノード側の状況を確認せずに
        「止まった」と報告してしまう。ここは実装に合わせて正直に書き、
        詳細画面のバッチ表で確認させる。
      */}
      <Alert variant="warning">
        <AlertDescription>
          停止すると未払い出しの宛先は抑止され、<strong>再開できません</strong>。
          ただし<strong>既にノードへ払い出したバッチは止まりません</strong>
          （ノードは持っていった分を注入し終えてから停止します）。
          停止後は配信の詳細画面で「払い出し待ち・払い出し済み」が残っていないかを必ず確認してください。
          理由は監査ログに残り、契約者へも通知されます（黙って止めると「途中で消えた」としか見えません）。
        </AlertDescription>
      </Alert>

      <Field label="停止の理由" required hint="5 文字以上。契約者への通知本文に載ります。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="苦情率が 0.5% を超えたため（同一ノードの他契約者への影響を回避）"
        />
      </Field>
    </FormDialog>
  );
}

/* --------------------------------------------------------- キルスイッチ */

function KillSwitchPanel({ canHalt }: { canHalt: boolean }): React.ReactElement | null {
  const [engageOpen, setEngageOpen] = React.useState(false);
  const [releaseOpen, setReleaseOpen] = React.useState(false);

  const query = useQuery({
    queryKey: qk.killSwitch(),
    queryFn: () => api.get<BulkKillSwitchView>('/bulk/kill-switch'),
    refetchInterval: 30_000,
    enabled: canHalt,
  });

  if (!canHalt) return null;
  const state = query.data;

  return (
    <Section
      title="全社キルスイッチ"
      description="全組織（または指定した組織）の一斉配信を止めます。投入は 1 名で、解除には別の運営者の承認が要ります。"
      className="mb-5"
      actions={
        state?.enabled ? (
          <Button variant="outline" size="sm" onClick={() => setReleaseOpen(true)}>
            <Power aria-hidden />
            解除する
          </Button>
        ) : (
          <Button variant="destructive" size="sm" onClick={() => setEngageOpen(true)}>
            <AlertOctagon aria-hidden />
            投入する
          </Button>
        )
      }
    >
      {state?.failSafe ? (
        <Alert variant="destructive" className="mb-3">
          <AlertTitle>設定値を読めなかったため、安全側（停止）に倒しています</AlertTitle>
          <AlertDescription>
            <code className="font-mono">system_settings.bulk_kill_switch</code> の形が想定と違います。
            投入した覚えが無いのに配信が止まっている場合、原因はこれです。
          </AlertDescription>
        </Alert>
      ) : null}

      {state?.enabled ? (
        <Alert variant="destructive">
          <AlertTitle>
            一斉配信を停止中（
            {state.organizationIds ? `${state.organizationIds.length} 組織` : '全組織'}）
          </AlertTitle>
          <AlertDescription className="space-y-1">
            <p>理由: {state.reason ?? '—'}</p>
            <p className="text-xs">
              投入: {state.engagedBy ?? '—'} / {formatDateTime(state.engagedAt)}
            </p>
          </AlertDescription>
        </Alert>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="success" size="sm">
            正常
          </Badge>
          一斉配信は通常どおり動作しています。
          <Send className="size-4" aria-hidden />
        </div>
      )}

      <EngageDialog open={engageOpen} onOpenChange={setEngageOpen} />
      <ReleaseDialog open={releaseOpen} onOpenChange={setReleaseOpen} />
    </Section>
  );
}

function EngageDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [scope, setScope] = React.useState<'all' | 'orgs'>('all');
  const [orgIds, setOrgIds] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setReason('');
    setScope('all');
    setOrgIds('');
  }, [open]);

  const engage = useMutation({
    mutationFn: () =>
      api.post('/bulk/kill-switch', {
        enabled: true,
        reason: reason.trim(),
        // 1 契約者の暴走で全契約者を巻き添えにしないため、範囲を絞れるようにする。
        ...(scope === 'orgs'
          ? {
              organizationIds: orgIds
                .split(/[\s,]+/)
                .map((s) => s.trim())
                .filter((s) => s.length > 0),
            }
          : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.killSwitch() });
      onOpenChange(false);
    },
  });

  const ids = orgIds.split(/[\s,]+/).filter((s) => s.trim().length > 0);
  const invalid = reason.trim().length < 5 || (scope === 'orgs' && (ids.length === 0 || ids.length > 200));

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="キルスイッチを投入する"
      description="投入は 1 名で行えます（止めるのは安全側なので承認を待たせません）。運営者全員に通知されます。"
      submitLabel="投入する"
      pending={engage.isPending}
      error={engage.error}
      disabled={invalid}
      onSubmit={() => engage.mutate()}
    >
      <Field label="範囲" required>
        <NativeSelect
          value={scope}
          onChange={(v) => setScope(v as 'all' | 'orgs')}
          options={[
            { value: 'all', label: '全組織' },
            { value: 'orgs', label: '指定した組織だけ' },
          ]}
        />
      </Field>

      {scope === 'orgs' ? (
        <Field label="組織 ID" required hint="改行・カンマ・空白区切りで最大 200 件。">
          <Textarea value={orgIds} onChange={(e) => setOrgIds(e.target.value)} rows={3} spellCheck={false} />
        </Field>
      ) : null}

      <Field label="理由" required hint="5 文字以上。監査ログと運営者への通知に載ります。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="送信元 IP が複数の RBL に掲載されたため、原因特定まで全配信を停止"
        />
      </Field>
    </FormDialog>
  );
}

function ReleaseDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [reason, setReason] = React.useState('');
  const [approver, setApprover] = React.useState('');

  const staff = useQuery({
    queryKey: qk.staff(),
    queryFn: () => api.get<CountedList<StaffUser>>('/staff'),
    enabled: open,
  });

  React.useEffect(() => {
    if (!open) return;
    setReason('');
    setApprover('');
  }, [open]);

  const release = useMutation({
    mutationFn: () =>
      api.post('/bulk/kill-switch', {
        enabled: false,
        reason: reason.trim(),
        approvedByStaffUserId: approver,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.killSwitch() });
      onOpenChange(false);
    },
  });

  // 自己承認を許すと「二名承認」という制約が意味を失う（サーバー側でも弾かれる）。
  const candidates = (staff.data?.items ?? []).filter((s) => s.id !== user?.id);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="キルスイッチを解除する"
      description="解除には別の運営者による承認が必要です。自分自身を承認者にすることはできません。"
      submitLabel="解除する"
      pending={release.isPending}
      error={release.error}
      disabled={reason.trim().length < 5 || approver.length === 0}
      onSubmit={() => release.mutate()}
    >
      <Field label="承認した運営者" required hint="口頭・チャットで承認を得たうえで選びます。">
        <NativeSelect
          value={approver}
          onChange={setApprover}
          options={[
            { value: '', label: '選択してください' },
            ...candidates.map((s) => ({ value: s.id, label: `${s.displayName}（${s.email}）` })),
          ]}
        />
      </Field>

      <Field label="解除の理由" required hint="5 文字以上。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="RBL からの削除を確認し、原因となった契約者の送信を個別に停止したため"
        />
      </Field>
    </FormDialog>
  );
}

/* ------------------------------------------------- 共有テンプレート（A321） */

/**
 * 運営が配る配信テンプレート（`content_templates.scope='platform'`）の管理。
 *
 * **`ops.content.template.write` という権限だけが定義されていて、使う先が
 * どこにも無かった。** 契約者面の `GET /portal/content-templates` は永久に空、
 * `apply-template` は必ず 404 で、CMS の初回体験が「白紙からの作成」しか
 * 無い状態だった。ここはその作成・公開の入口。
 *
 * **本文はこの画面では扱わない**（不変条件 9。実体は S3、CP の DB はメタのみ）。
 * テンプレートが指すのは既存の `content_revisions` の ID で、運営用の組織で
 * 作った文書のリビジョンを指定する運用にしてある。運営面に本文の編集画面を
 * もう 1 つ作らないための割り切りで、リビジョン ID は契約者パネル（CMS）の
 * 文書詳細から控える。
 *
 * **公開は作成と別操作**。`apply-template` は本文の**コピー**なので、一度
 * 配られたものは撤回しても取り消せない。出す前に見直す段階を必ず挟む。
 */
function TemplatesTab(): React.ReactElement {
  const [createOpen, setCreateOpen] = React.useState(false);
  const [retracting, setRetracting] = React.useState<ContentTemplateEntry | null>(null);

  const query = useQuery({
    queryKey: qk.contentTemplates(),
    queryFn: () => api.get<CountedList<ContentTemplateEntry>>('/content-templates'),
  });

  const items = query.data?.items ?? [];
  const visible = items.filter((t) => t.visible).length;

  const columns: Array<DataTableColumn<ContentTemplateEntry>> = [
    {
      id: 'name',
      header: 'テンプレート',
      cell: (t) => (
        <div className="min-w-0 space-y-0.5">
          <span className="block truncate font-medium">{t.name}</span>
          {t.description ? (
            <span className="block truncate text-xs text-muted-foreground">{t.description}</span>
          ) : null}
        </div>
      ),
    },
    {
      id: 'category',
      header: '分類',
      hideBelow: 'md',
      cell: (t) => t.category ?? <span className="text-xs text-muted-foreground">—</span>,
    },
    {
      id: 'visible',
      header: '契約者から',
      width: 'w-32',
      cell: (t) =>
        t.visible ? (
          <Badge variant="success" size="sm">
            見えている
          </Badge>
        ) : t.retractedAt ? (
          <Badge variant="muted" size="sm">
            撤回済み
          </Badge>
        ) : (
          <Badge variant="warning" size="sm">
            未公開
          </Badge>
        ),
    },
    {
      id: 'revision',
      header: '元リビジョン',
      hideBelow: 'lg',
      cell: (t) => <code className="font-mono text-xs text-muted-foreground">{t.sourceRevisionId}</code>,
    },
    {
      id: 'publishedAt',
      header: '公開日時',
      hideBelow: 'md',
      cell: (t) => (t.publishedAt ? formatDateTime(t.publishedAt) : '—'),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-28',
      cell: (t) =>
        t.visible ? (
          <Button variant="outline" size="sm" onClick={() => setRetracting(t)}>
            <EyeOff aria-hidden />
            撤回
          </Button>
        ) : (
          <PublishButton template={t} />
        ),
    },
  ];

  return (
    <div className="space-y-5">
      <Section
        title="共有テンプレート"
        description="契約者が CMS の新規作成時に選べる雛形です。公開したものだけが契約者から見えます。"
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus aria-hidden />
            テンプレートを追加
          </Button>
        }
      >
        <Alert>
          <AlertTitle>撤回しても、既に適用されたものは戻せません</AlertTitle>
          <AlertDescription>
            テンプレートの適用は<strong>本文のコピー</strong>です。撤回で消せるのは「これから選べること」だけで、
            すでに契約者の文書になったものには影響しません（誤った内容を配ってしまった場合は、
            契約者へ個別に連絡する必要があります）。
          </AlertDescription>
        </Alert>

        <div className="mt-4 flex gap-3 text-sm text-muted-foreground">
          <span>
            登録 <strong className="text-foreground">{formatCount(items.length)}</strong> 件
          </span>
          <span>
            公開中 <strong className="text-foreground">{formatCount(visible)}</strong> 件
          </span>
        </div>
      </Section>

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(t) => t.id}
        loading={query.isLoading}
        empty={
          <ListEmpty filtered={false} entityLabel="共有テンプレート" />
        }
      />

      <CreateTemplateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <RetractTemplateDialog template={retracting} onClose={() => setRetracting(null)} />
    </div>
  );
}

/** 公開（`POST /ops/content-templates/:id/publish` に `{published:true}`）。 */
function PublishButton({ template }: { template: ContentTemplateEntry }): React.ReactElement {
  const qc = useQueryClient();
  const publish = useMutation({
    mutationFn: () => api.post(`/content-templates/${template.id}/publish`, { published: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.contentTemplates() }),
  });

  return (
    <Button variant="outline" size="sm" loading={publish.isPending} onClick={() => publish.mutate()}>
      <Eye aria-hidden />
      公開
    </Button>
  );
}

/**
 * 新規登録（`POST /ops/content-templates`）。
 *
 * 作った時点では**非公開**（`published_at` は null）。サーバーは元リビジョンの
 * `upload_state` が `stored` でなければ 400 にするので、下書き途中の
 * リビジョン ID を入れると弾かれる。
 */
function CreateTemplateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [name, setName] = React.useState('');
  const [sourceRevisionId, setSourceRevisionId] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [description, setDescription] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setName('');
    setSourceRevisionId('');
    setCategory('');
    setDescription('');
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      // body は `.strict()`。未定義のキーを混ぜると 400 になるので、
      // 空欄は送らない（`undefined` はシリアライズで落ちる）。
      api.post('/content-templates', {
        name: name.trim(),
        sourceRevisionId: sourceRevisionId.trim(),
        category: category.trim() === '' ? undefined : category.trim(),
        description: description.trim() === '' ? undefined : description.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.contentTemplates() });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="共有テンプレートを追加"
      description="登録した時点では非公開です。内容を見直してから公開してください。"
      submitLabel="登録する"
      pending={create.isPending}
      error={create.error}
      disabled={name.trim() === '' || sourceRevisionId.trim() === ''}
      onSubmit={() => create.mutate()}
    >
      <Field label="名前" required hint="契約者の選択肢に出ます（例: 月次ニュースレター）。">
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
      </Field>

      <Field
        label="元になるリビジョン ID"
        required
        hint="運営用の組織で作った文書のリビジョン ID。本文の保存が完了していない（stored でない）リビジョンは登録できません。"
      >
        <Input
          value={sourceRevisionId}
          onChange={(e) => setSourceRevisionId(e.target.value)}
          className="font-mono"
          placeholder="01J..."
        />
      </Field>

      <Field label="分類" hint="一覧の並びと絞り込みに使います（例: newsletter / announcement）。">
        <Input value={category} onChange={(e) => setCategory(e.target.value)} maxLength={60} />
      </Field>

      <Field label="説明" hint="どういうときに使う雛形かを 1〜2 文で。契約者が読みます。">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={500} />
      </Field>
    </FormDialog>
  );
}

/**
 * 撤回（`{published:false}`）。
 *
 * 「これから選べること」を消すだけとはいえ、契約者の作業導線が黙って
 * 減る操作なので、名前の完全一致入力を要求する（既存の破壊的操作の作法）。
 */
function RetractTemplateDialog({
  template,
  onClose,
}: {
  template: ContentTemplateEntry | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const retract = useMutation({
    mutationFn: () => api.post(`/content-templates/${template?.id ?? ''}/publish`, { published: false }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.contentTemplates() });
      onClose();
    },
  });

  return (
    <ConfirmTextDialog
      open={template !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="テンプレートを撤回する"
      description="撤回すると契約者の選択肢から消えます。すでに適用された文書は変わりません（適用は本文のコピーのため）。"
      confirmText={template?.name ?? ''}
      confirmLabel="撤回する"
      pending={retract.isPending}
      error={retract.error}
      onConfirm={() => retract.mutate()}
    />
  );
}
